import { useState } from "react";
import { useBrandContext } from "@/contexts/brand-context";
import { usePermissions } from "@/hooks/use-permissions";
import { useAuth } from "@/hooks/use-auth";
import { useTranslation } from "react-i18next";
import { useMembers, useInviteMember, useUpdateMemberRole, useRemoveMember } from "@/hooks/use-members";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { MemberRow } from "@/components/settings/MemberRow";
import { InviteMemberDialog } from "@/components/settings/InviteMemberDialog";
import { toast } from "sonner";

export function BrandSettingsTeamPage() {
  const { t } = useTranslation("settings");
  const { brand } = useBrandContext();
  const { canManageBrand } = usePermissions();
  const { user } = useAuth();
  const brandId = brand?.id ?? "";
  const canInvite = canManageBrand && user?.isSuperAdmin;

  const { data: members, isLoading } = useMembers(brandId);
  const inviteMember = useInviteMember(brandId);
  const updateRole = useUpdateMemberRole(brandId);
  const removeMember = useRemoveMember(brandId);

  const [inviteOpen, setInviteOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="px-6 py-8 w-full space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  async function handleRemove(userId: string, email: string) {
    if (!confirm(t("team.removeConfirm", { email }))) return;
    try {
      await removeMember.mutateAsync(userId);
      toast.success(t("team.removed", { email }));
    } catch (e: any) {
      toast.error(e.message ?? t("team.failedRemove"));
    }
  }

  return (
    <div className="px-6 py-8 w-full space-y-6">
      <header className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{t("team.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("team.subtitle")}</p>
        </div>
        {canInvite && (
          <Button onClick={() => setInviteOpen(true)}>{t("team.inviteMember")}</Button>
        )}
      </header>

      <section className="space-y-2">
        {(members ?? []).map((m) => (
          <MemberRow
            key={m.id}
            member={m}
            currentUserId={user?.id ?? ""}
            canEdit={canManageBrand}
            onRoleChange={(role) => updateRole.mutate({ userId: m.userId, role })}
            onRemove={() => handleRemove(m.userId, m.email)}
          />
        ))}
        {(members ?? []).length === 0 && (
          <div className="rounded border border-dashed p-8 text-center text-sm text-muted-foreground">
            {t("team.noMembers")}
          </div>
        )}
      </section>

      <InviteMemberDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        onSubmit={(input) => inviteMember.mutateAsync(input)}
      />
    </div>
  );
}
